import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils/cn';
import { dueDateStatus, formatDayKey, isDebtNote } from '../utils/cobroHelpers';

// La clave es 'Vence el {{vencimiento}}' y NO 'Vence el {{fecha}}': esa ya existe en
// _missing.json con el sentido de "caduca" (documentos de cumplimiento: "Expires on…") y el
// merge plano de los locales la pisaría en esas pantallas. Distinto nombre de variable,
// distinta clave, y cada una conserva su traducción.

/**
 * Subtexto de vencimiento bajo el estado de una nota en un listado:
 * "Vencida hace N d" (vencida), "Venció el dd-mm-aaaa" (en revisión con la fecha pasada) o
 * "Vence el dd-mm-aaaa" (deuda con vencimiento). No dibuja nada si la nota no tiene
 * vencimiento o ya no es deuda.
 *
 * Todo sale de campos que calcula billing (`isOverdue`, `daysOverdue`, `dueDateLocal`).
 */
export function DueDateHint({ note, className }) {
  const { t } = useTranslation();
  const info = dueDateStatus(note);
  if (!info) return null;
  if (info.kind === 'overdue') {
    return (
      <span
        className={cn('block text-xs font-medium text-status-error', className)}
        title={info.dueDate ? t('Venció el {{vencimiento}}', { vencimiento: info.dueDate }) : undefined}
      >
        {t('Vencida hace {{n}} d', { n: info.days })}
      </span>
    );
  }
  return (
    <span className={cn('block text-xs text-fg-muted', className)}>
      {info.kind === 'lapsed'
        ? t('Venció el {{vencimiento}}', { vencimiento: info.dueDate })
        : t('Vence el {{vencimiento}}', { vencimiento: info.dueDate })}
    </span>
  );
}

const TERM_SOURCE_LABELS = {
  commerce: 'regla del comercio',
  // Nota consolidada de un holding con plazo propio en su perfil de cobro.
  holding: 'perfil del holding',
  operator: 'plazo general del operador',
};

/**
 * Línea de vencimiento para el detalle de una nota: fecha, plazo y, si está vencida, hace
 * cuántos días. `showSource` agrega de dónde salió el plazo (solo tiene sentido para el
 * operador: al comercio no le dice nada).
 */
export function NoteDueDateLine({ note, showSource = false, className }) {
  const { t } = useTranslation();
  if (!note || note.status === 'cancelled' || note.paymentStatus === 'cancelled') return null;
  const dueDate = formatDayKey(note.dueDateLocal);

  if (!dueDate) {
    // Una nota vieja emitida antes de que existieran los plazos: vale aclararlo solo si
    // todavía se debe; en una pagada no le importa a nadie.
    return isDebtNote(note) ? (
      <div className={cn('bill-doc-head__meta', className)}>{t('Sin fecha de vencimiento')}</div>
    ) : null;
  }

  const termDays = Number(note.paymentTermDays);
  const sourceLabel = showSource ? TERM_SOURCE_LABELS[note.paymentTermSource] : undefined;
  return (
    <div className={cn('bill-doc-head__meta', className)}>
      {t('Vencimiento')}: <strong>{dueDate}</strong>
      {termDays > 0 && <> · {t('Plazo de {{n}} días', { n: termDays })}</>}
      {sourceLabel && <> ({t(sourceLabel)})</>}
      {note.isOverdue === true && (
        <span className="font-medium text-status-error">
          {' · '}
          {t('Vencida hace {{n}} d', { n: Number(note.daysOverdue) || 0 })}
        </span>
      )}
    </div>
  );
}
